import { useMutation } from '@apollo/client';
import { Box, Stack } from '@mui/material';
import { Cell, Text } from '@telegram-apps/telegram-ui';
import { hapticFeedback } from '@tma.js/sdk';
import dayjs from 'dayjs';
import { type FC, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

import { PurchaseRewardMutation, PurchaseRewardMutationVariables } from '../client/generated/graphql';
import { purchaseRewardMutation } from '../client/mutations/purchaseReward';
import { ListReward } from '../client/types';
import { toDueDate } from '../helpers/datetime';
import { getRewardColor } from '../helpers/reward';
import { rewardViewPath } from '../navigation/paths';
import { BaseButton } from './common/BaseButton';
import { CommonIcon } from './common/CommonIcon';
import { CustomSection } from './common/CustomSection';

interface RewardDisplayProps {
  reward: ListReward;
  collectedCurrency: number;
}

export const RewardDisplay: FC<RewardDisplayProps> = ({ 
  reward, 
  collectedCurrency
}) => {
  const navigate = useNavigate();
  const isPurchased = reward.rewardedOn !== null;
  const canPurchase = reward.price <= collectedCurrency;
  
  const [purchaseReward, { loading: isPurchasing }] = useMutation<PurchaseRewardMutation, PurchaseRewardMutationVariables>(purchaseRewardMutation);

  const handleRewardClick = () => {
    hapticFeedback.impactOccurred.ifAvailable('light');
    const path = rewardViewPath.replace(':id', reward.id);
    navigate(path);
  };

  const handlePurchaseClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (reward.isRewarded || isPurchasing || !canPurchase) return;
    
    hapticFeedback.impactOccurred.ifAvailable('light');
    
    try {
      await purchaseReward({ variables: { rewardId: reward.id } });
    } catch (error) {
      console.error('Error purchasing reward:', error);
    }
  }, [reward.id, reward.isRewarded, isPurchasing, canPurchase, purchaseReward]);

  return (
    <CustomSection>
      <Cell
        style={{ height: '64px', paddingRight: isPurchased ? undefined : '0px' }}
        onClick={handleRewardClick}
        before={
          <Box sx={{
            width: 40,
            height: 40,
            borderRadius: 2,
            background: getRewardColor(reward.rarity),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <CommonIcon 
              icon={reward.icon!} 
              type="filled" 
              size={28} 
              color="var(--tg-theme-text-color)" 
            />
          </Box>
        }
        after={
          isPurchased ? (
            <Stack direction="column" gap={0.5} alignItems="flex-end">
              <Stack direction="row" gap={0.5} alignItems="center">
                <CommonIcon icon='paid' type="filled" size={20} color="var(--tg-theme-button-color)" />
                <Text 
                  weight='2' 
                  color={'var(--tg-theme-text-color)'} 
                  style={{ fontSize: '14px' }}
                >
                  {reward.price}
                </Text>
              </Stack>
              {isPurchased && <Text 
                style={{ fontSize: '12px', color: 'var(--tgui--hint_color)', whiteSpace: 'nowrap' }}
              >
                {toDueDate(dayjs(reward.rewardedOn))}
              </Text>}
            </Stack>
          ) : (
            <BaseButton loading={isPurchasing} onClick={handlePurchaseClick} disabled={!canPurchase} style={{ height: '64px', width: '70px', borderRadius: '0px 8px 8px 0px', backgroundColor: 'var(--tg-theme-button-color)' }}>
              <Stack direction="column" gap={0.5} alignItems="center">
                <CommonIcon icon='paid' type="filled" size={20} color={'var(--tg-theme-text-color)'} />
                <Text weight='2' style={{ fontSize: '14px' }}>
                  {reward.price}
                </Text>
              </Stack>
            </BaseButton>
          )
        }
        subtitle={reward.description || ''}
      >
        {reward.name}
      </Cell>
    </CustomSection>
  );
}; 